import React, { useState, useEffect } from 'react';
import {
  Box, Button, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Checkbox, Alert, CircularProgress, Chip
} from '@mui/material';
import { Save, RestartAlt } from '@mui/icons-material';
import {
  CONTRACT_TYPES, FIELD_SECTIONS, ALL_FIELD_KEYS, ALWAYS_VISIBLE,
  buildDefaultConfig, type VisibilityConfig
} from '../../config/fieldVisibilityConfig';
import { adminApi } from '../../services/api';

function mergeWithDefaults(saved: VisibilityConfig): VisibilityConfig {
  const full = buildDefaultConfig();
  for (const ct of CONTRACT_TYPES) {
    if (saved[ct]) {
      for (const key of ALL_FIELD_KEYS) {
        if (saved[ct][key] !== undefined) {
          full[ct][key] = saved[ct][key];
        }
      }
    }
  }
  return full;
}

export default function FieldVisibilityTab() {
  const [config, setConfig] = useState<VisibilityConfig>(buildDefaultConfig());
  const [savedJson, setSavedJson] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    adminApi.getSettings()
      .then(res => {
        const row = (res.data as any[]).find((s: any) => s.key === 'field_visibility');
        if (row?.value) {
          try {
            const parsed = JSON.parse(row.value);
            const merged = mergeWithDefaults(parsed);
            setConfig(merged);
            setSavedJson(JSON.stringify(merged));
          } catch {
            const def = buildDefaultConfig();
            setConfig(def);
            setSavedJson(JSON.stringify(def));
          }
        } else {
          const def = buildDefaultConfig();
          setConfig(def);
          setSavedJson(JSON.stringify(def));
        }
      })
      .catch(() => {
        const def = buildDefaultConfig();
        setConfig(def);
        setSavedJson(JSON.stringify(def));
      })
      .finally(() => setLoading(false));
  }, []);

  const isDirty = JSON.stringify(config) !== savedJson;

  function toggle(contractType: string, fieldKey: string) {
    if (ALWAYS_VISIBLE.has(fieldKey)) return;
    setConfig(prev => ({
      ...prev,
      [contractType]: {
        ...prev[contractType],
        [fieldKey]: !prev[contractType]?.[fieldKey]
      }
    }));
    setMessage(null);
  }

  function toggleColumn(contractType: string) {
    const typeConfig = config[contractType] || {};
    const toggleableKeys = ALL_FIELD_KEYS.filter(k => !ALWAYS_VISIBLE.has(k));
    const allChecked = toggleableKeys.every(k => typeConfig[k] !== false);
    setConfig(prev => {
      const updated = { ...prev[contractType] };
      toggleableKeys.forEach(k => { updated[k] = !allChecked; });
      return { ...prev, [contractType]: updated };
    });
    setMessage(null);
  }

  async function handleSave() {
    setSaving(true);
    setMessage(null);
    try {
      // Strip out fields that are true (only store false overrides to keep JSON small)
      const sparse: VisibilityConfig = {};
      for (const ct of CONTRACT_TYPES) {
        const overrides: Record<string, boolean> = {};
        for (const key of ALL_FIELD_KEYS) {
          if (!ALWAYS_VISIBLE.has(key) && config[ct]?.[key] === false) {
            overrides[key] = false;
          }
        }
        if (Object.keys(overrides).length > 0) {
          sparse[ct] = overrides;
        }
      }
      await adminApi.updateSetting('field_visibility', JSON.stringify(sparse));
      setSavedJson(JSON.stringify(config));
      setMessage({ type: 'success', text: 'Field visibility saved.' });
    } catch {
      setMessage({ type: 'error', text: 'Failed to save.' });
    } finally {
      setSaving(false);
    }
  }

  function handleReset() {
    const def = buildDefaultConfig();
    setConfig(def);
    setMessage(null);
  }

  if (loading) return <Box py={4} textAlign="center"><CircularProgress /></Box>;

  return (
    <Box>
      {/* Toolbar */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="body2" color="text.secondary">
            Uncheck a field to hide it on contract forms for that type.
          </Typography>
          {isDirty && <Chip label="Unsaved changes" color="warning" size="small" />}
        </Box>
        <Box display="flex" gap={1}>
          <Button size="small" startIcon={<RestartAlt />} onClick={handleReset}>
            Reset to Defaults
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />}
            onClick={handleSave}
            disabled={!isDirty || saving}
          >
            Save
          </Button>
        </Box>
      </Box>

      {message && <Alert severity={message.type} sx={{ mb: 2 }}>{message.text}</Alert>}

      {/* Matrix table */}
      <TableContainer sx={{ maxHeight: 'calc(100vh - 320px)', border: 1, borderColor: 'divider', borderRadius: 1 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, minWidth: 220, position: 'sticky', left: 0, zIndex: 3, bgcolor: 'background.paper' }}>
                Field
              </TableCell>
              {CONTRACT_TYPES.map(ct => {
                const toggleableKeys = ALL_FIELD_KEYS.filter(k => !ALWAYS_VISIBLE.has(k));
                const allChecked = toggleableKeys.every(k => config[ct]?.[k] !== false);
                return (
                  <TableCell key={ct} align="center" sx={{ fontWeight: 600, minWidth: 80 }}>
                    <Box>
                      {ct}
                      <Box>
                        <Checkbox
                          size="small"
                          checked={allChecked}
                          indeterminate={!allChecked && toggleableKeys.some(k => config[ct]?.[k] !== false)}
                          onChange={() => toggleColumn(ct)}
                          sx={{ p: 0 }}
                        />
                      </Box>
                    </Box>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {FIELD_SECTIONS.map(section => (
              <React.Fragment key={section.section}>
                {/* Section header row */}
                <TableRow>
                  <TableCell
                    colSpan={CONTRACT_TYPES.length + 1}
                    sx={{ bgcolor: 'action.hover', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', py: 0.75, position: 'sticky', left: 0 }}
                  >
                    {section.section}
                  </TableCell>
                </TableRow>
                {/* Field rows */}
                {section.fields.map(field => {
                  const locked = ALWAYS_VISIBLE.has(field.key);
                  return (
                    <TableRow key={field.key} hover>
                      <TableCell sx={{ position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1, borderRight: 1, borderColor: 'divider' }}>
                        <Typography variant="body2" color={locked ? 'text.disabled' : 'text.primary'}>
                          {field.label} {locked && <Typography component="span" variant="caption" color="text.disabled">(required)</Typography>}
                        </Typography>
                      </TableCell>
                      {CONTRACT_TYPES.map(ct => (
                        <TableCell key={ct} align="center" sx={{ py: 0.25 }}>
                          <Checkbox
                            size="small"
                            checked={locked || config[ct]?.[field.key] !== false}
                            disabled={locked}
                            onChange={() => toggle(ct, field.key)}
                            sx={{ p: 0.25 }}
                          />
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
